import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import caseService from '../services/caseService';
import '../styles/ReportPage.css';

export default function CombinedReportPage() {
  const { caseId } = useParams();
  const [blobUrl, setBlobUrl] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const blobRef = useRef(null);
  const mountedRef = useRef(true);
  const navigate = useNavigate();

  // ── Cleanup blob URLs on unmount ─────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (blobRef.current && blobRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, []);

  // ── Reset state fully when caseId changes ────────────────────
  useEffect(() => {
    setBlobUrl(null);
    setCaseData(null);
    setErr(null);

    if (blobRef.current && blobRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
    }
  }, [caseId]);

  const loadReport = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setErr(null);
    setBlobUrl(null);
    setCaseData(null);

    try {
      const [response, caseInfo] = await Promise.all([
        api.get(`/reports/${caseId}/combined/`, {
          responseType: 'blob',
        }),
        caseService.getById(caseId).catch(() => null),
      ]);

      if (!mountedRef.current) return;

      if (blobRef.current && blobRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(blobRef.current);
      }
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      blobRef.current = url;

      setBlobUrl(url);
      setCaseData(caseInfo);

    } catch (e) {
      if (!mountedRef.current) return;
      const status = e?.response?.status;
      if (status === 404) {
        setErr('Combined report not found. The case may still be processing — please try again in a moment.');
      } else if (status === 401 || status === 403) {
        setErr('Session expired. Please log in again.');
      } else {
        setErr(`Failed to open combined report (${status || 'network error'}). Please try again.`);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleDownload = async () => {
    try {
      const response = await api.get(`/reports/${caseId}/combined/`, {
        responseType: 'blob',
      });
      const blobUrlLocal = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const patientName = caseData?.patient_name || 'Patient';
      const patientId = caseData?.patient_id_display || caseId;
      const a = document.createElement('a');
      a.href = blobUrlLocal;
      a.download = `Combined_Report_${patientName}_${patientId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrlLocal), 2000);
    } catch (error) {
      alert('Failed to download PDF.');
    }
  };

  if (loading) {
    return (
      <div className="rep__container rep__loading-container">
        <div className="rep__loading-wrapper">
          <i className="fa-solid fa-spinner fa-spin fa-2x rep__loading-spinner" />
          <div className="rep__loading-text">Loading combined report…</div>
        </div>
      </div>
    );
  }

  if (err && !loading) {
    return (
      <div className="rep__container rep__error-container">
        <div className="rep__error-wrapper">
          <i className="fa-solid fa-file-circle-xmark fa-2x rep__error-icon" />
          <div className="rep__error-title">Report Unavailable</div>
          <div className="rep__error-message">{err}</div>
          <div className="rep__error-actions">
            <button className="btn-outline-pink" onClick={loadReport}>
              <i className="fa-solid fa-rotate-right" />Retry
            </button>
            <button className="btn-outline-pink" onClick={() => navigate('/cases')}>
              <i className="fa-solid fa-arrow-left" />Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rep__container">
      <div className="rep__main-pane" style={{ width: '100%' }}>
        <div className="rep__main-header">
          <div className="rep__main-title">Combined Report Preview</div>
          <button
            className="rep__download-btn"
            onClick={handleDownload}
            disabled={!blobUrl}
          >
            Download
          </button>
        </div>

        <div className="rep__pdf-container">
          {blobUrl ? (
            <iframe
              key={blobUrl}
              scrolling="no"
              src={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              title="Combined Report PDF"
              className="rep__pdf-preview"
              style={{ width: '100%', height: '100%', border: 'none', overflow: 'hidden' }}
            />
          ) : (
            <div className="rep__empty-state">
              <div className="rep__empty-title">Report preview not available</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
